"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
/**
 * Module: relativeTime.ts
 *
 * This module provides utility functions for date and time calculations. This module is originally from intl-relativeformat, which was
 * deprecated in favor of the official @formatjs/intl-relativetimeformat package.
 *
 * This module should be used only for the legacy intl package, and should not be used for new development.
 *
 * Functions:
 * - daysToYears(days: number): Converts the given number of days to years, taking into account leap years.
 * - differenceInCalendarDays(dirtyDateLeft: number, dirtyDateRight: number): Calculates the difference in calendar days between two dates.
 * - startOfDay(dirtyDate: number): Returns a new Date object representing the start of the day for the given date.
 * - relativeTimeFields(from: number, to: number): Calculates relative time fields for the given time range.
 */

var round = Math.round;
var MILLISECONDS_IN_MINUTE = 60000;
var MILLISECONDS_IN_DAY = 86400000;

/**
 * Converts the given number of days to years.
 *
 * @param days The number of days to convert.
 * @returns The equivalent number of years.
 */
function daysToYears(days) {
  // 400 years have 146097 days (taking into account leap year rules)
  return days * 400 / 146097;
}

/**
 * Returns a new Date object representing the start of the day for the given date.
 *
 * @param dirtyDate - The date for which to get the start of the day.
 * @returns A new Date object representing the start of the day.
 */
function startOfDay(dirtyDate) {
  var date = new Date(dirtyDate);
  date.setHours(0, 0, 0, 0);
  return date;
}

/**
 * Calculates the difference in calendar days between two dates.
 *
 * @param dirtyDateLeft - The left date.
 * @param dirtyDateRight - The right date.
 * @returns The difference in calendar days between the two dates.
 */
function differenceInCalendarDays(dirtyDateLeft, dirtyDateRight) {
  var startOfDayLeft = startOfDay(dirtyDateLeft);
  var startOfDayRight = startOfDay(dirtyDateRight);
  var timestampLeft = startOfDayLeft.getTime() - startOfDayLeft.getTimezoneOffset() * MILLISECONDS_IN_MINUTE;
  var timestampRight = startOfDayRight.getTime() - startOfDayRight.getTimezoneOffset() * MILLISECONDS_IN_MINUTE;
  return round((timestampLeft - timestampRight) / MILLISECONDS_IN_DAY);
}

/**
 * Calculates relative time fields for the given time range.
 * @param from - The starting time in milliseconds.
 * @param to - The ending time in milliseconds.
 * @returns An object containing the calculated options for each unit of time.
 */
function relativeTimeFields(from, to) {
  // split into the following units: year, month, week, day, hour, minute, second, millisecond
  var millisecond = round(to - from);
  var second = round(millisecond / 1000);
  var minute = round(second / 60);
  var hour = round(minute / 60);
  var day = differenceInCalendarDays(to, from);
  var week = round(day / 7);
  var rawYears = daysToYears(day);
  var month = round(rawYears * 12);
  var year = round(rawYears);
  return {
    millisecond: millisecond,
    second: second,
    'second-short': second,
    minute: minute,
    'minute-short': minute,
    hour: hour,
    'hour-short': hour,
    day: day,
    'day-short': day,
    week: week,
    'week-short': week,
    month: month,
    'month-short': month,
    year: year,
    'year-short': year
  };
}
var _default = exports.default = relativeTimeFields;
module.exports = exports.default;
//# sourceMappingURL=relativeTimeFields.js.map